<template>
  <Teleport to="body">
    <Transition name="lightbox">
      <div v-if="modelValue" class="lightbox-overlay" @click.self="close" @wheel.prevent="onWheel">
        <div class="lightbox-img-box">
          <img
            ref="imgRef"
            :src="modelValue"
            class="lightbox-img rounded-lg"
            :class="{ 'is-zoomed': zoomed, 'is-dragging': dragging }"
            :style="{ transform: `translate(${tx}px, ${ty}px) scale(${scale})` }"
            draggable="false"
            @click.stop
            @dblclick.prevent="onDblClick"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="endDrag"
            @pointercancel="endDrag"
          />
        </div>

        <div class="lightbox-controls" @click.stop>
          <button
            type="button"
            class="lightbox-control-btn"
            :title="t('chatbot.image-zoom-out')"
            @click="zoomBy(1 / ZOOM_STEP)"
          >
            <v-icon size="22" color="white">mdi-magnify-minus-outline</v-icon>
          </button>
          <span class="lightbox-zoom-level">{{ Math.round(scale * 100) }}%</span>
          <button type="button" class="lightbox-control-btn" :title="t('chatbot.image-zoom-in')" @click="zoomBy(ZOOM_STEP)">
            <v-icon size="22" color="white">mdi-magnify-plus-outline</v-icon>
          </button>
          <button type="button" class="lightbox-control-btn" :title="t('chatbot.image-zoom-reset')" @click="reset">
            <v-icon size="22" color="white">mdi-fit-to-screen-outline</v-icon>
          </button>
        </div>

        <button type="button" class="lightbox-close" :title="t('chatbot.image-close')" @click="close">
          <v-icon size="28" color="white">mdi-close</v-icon>
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ modelValue: string }>();
const emit = defineEmits<{ "update:modelValue": [src: string] }>();

const { t } = useI18n();

const ZOOM_STEP = 1.4;
const MAX_SCALE = 8;
/** Pixels of image kept on screen when panning, so it can't be lost off-view. */
const MIN_VISIBLE = 80;

const imgRef = ref<HTMLImageElement>();
const scale = ref(1);
const tx = ref(0);
const ty = ref(0);
const dragging = ref(false);
const zoomed = computed(() => scale.value > 1);

let pointerId: number | null = null;
let lastX = 0;
let lastY = 0;

function close() {
  emit("update:modelValue", "");
}

function reset() {
  scale.value = 1;
  tx.value = 0;
  ty.value = 0;
}

// Rescales while keeping the (focusX, focusY) viewport point anchored on the
// same image pixel: the image center moves by (focus - center) * (1 - ratio).
function setScale(next: number, focusX?: number, focusY?: number) {
  const img = imgRef.value;
  const clamped = Math.min(MAX_SCALE, Math.max(1, next));
  if (!img || clamped === scale.value) return;
  if (clamped === 1) {
    reset();
    return;
  }
  const rect = img.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const ratio = clamped / scale.value;
  tx.value += ((focusX ?? cx) - cx) * (1 - ratio);
  ty.value += ((focusY ?? cy) - cy) * (1 - ratio);
  scale.value = clamped;
  clampPan();
}

function zoomBy(factor: number, focusX?: number, focusY?: number) {
  setScale(scale.value * factor, focusX, focusY);
}

function onWheel(event: WheelEvent) {
  zoomBy(event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP, event.clientX, event.clientY);
}

// Double-click: jump to 100% natural pixels (wide charts become readable),
// or back to fit when already zoomed.
function onDblClick(event: MouseEvent) {
  if (zoomed.value) {
    reset();
    return;
  }
  const img = imgRef.value;
  const native = img ? img.naturalWidth / img.clientWidth : 2;
  setScale(Math.max(2, native), event.clientX, event.clientY);
}

function onPointerDown(event: PointerEvent) {
  if (!zoomed.value) return;
  pointerId = event.pointerId;
  lastX = event.clientX;
  lastY = event.clientY;
  dragging.value = true;
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
}

function onPointerMove(event: PointerEvent) {
  if (!dragging.value || event.pointerId !== pointerId) return;
  tx.value += event.clientX - lastX;
  ty.value += event.clientY - lastY;
  lastX = event.clientX;
  lastY = event.clientY;
  clampPan();
}

function endDrag(event: PointerEvent) {
  if (event.pointerId !== pointerId) return;
  dragging.value = false;
  pointerId = null;
}

function clampPan() {
  const img = imgRef.value;
  if (!img) return;
  const maxX = (img.clientWidth * scale.value + window.innerWidth) / 2 - MIN_VISIBLE;
  const maxY = (img.clientHeight * scale.value + window.innerHeight) / 2 - MIN_VISIBLE;
  tx.value = Math.min(maxX, Math.max(-maxX, tx.value));
  ty.value = Math.min(maxY, Math.max(-maxY, ty.value));
}

// Close with the Escape key while open (mirrors ChatbotMapAsset), and start
// every viewing from the fitted state.
function onKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    close();
  }
}

watch(
  () => props.modelValue,
  (src) => {
    reset();
    if (src) {
      window.addEventListener("keydown", onKeydown);
    } else {
      window.removeEventListener("keydown", onKeydown);
    }
  },
);

onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown));
</script>

<style scoped>
.lightbox-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
}

.lightbox-img {
  display: block;
  max-width: 90vw;
  max-height: 85vh;
  cursor: default;
  transition: transform 0.15s ease;
  will-change: transform;
  user-select: none;
  touch-action: none;
}

.lightbox-img.is-zoomed {
  cursor: grab;
}

.lightbox-img.is-dragging {
  cursor: grabbing;
  transition: none;
}

.lightbox-controls {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.5);
}

.lightbox-control-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.lightbox-control-btn:hover {
  background: rgba(255, 255, 255, 0.15);
}

.lightbox-zoom-level {
  color: white;
  font-size: 0.8rem;
  min-width: 48px;
  text-align: center;
  user-select: none;
}

.lightbox-close {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.5);
  cursor: pointer;
}

.lightbox-close:hover {
  background: rgba(0, 0, 0, 0.75);
}

.lightbox-enter-active,
.lightbox-leave-active {
  transition: opacity 0.2s ease;
}

.lightbox-enter-active .lightbox-img-box,
.lightbox-leave-active .lightbox-img-box {
  transition: transform 0.2s ease;
}

.lightbox-enter-from,
.lightbox-leave-to {
  opacity: 0;
}

.lightbox-enter-from .lightbox-img-box,
.lightbox-leave-to .lightbox-img-box {
  transform: scale(0.9);
}
</style>
